import { useCallback, useEffect, useMemo } from "react"

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { fetchSegmentViewPrefs, saveSegmentViewPrefs } from "../api/segment-view-prefs-api"
import {
  loadViewPrefs,
  sanitizeViewPrefs,
  saveViewPrefs,
  type SegmentViewPrefs,
} from "../utils/segment-view-prefs"

/** Oid-scoped so an in-session identity switch can never bleed one user's
 * cached prefs into another's render (the localStorage cache is oid-keyed too). */
export function segmentViewPrefsQueryKey(oid: string | null) {
  return ["segment-analysis", "view-prefs", oid ?? "anon"] as const
}

/**
 * The user layer (§G9), Phase 3 (#2070): per-user view prefs roam via the
 * backend (source of truth). localStorage keyed by oid survives ONLY as a paint
 * cache of the last backend response, so a hard reload renders the user's
 * cards/charts instantly instead of flashing defaults until the API answers.
 *
 * Returns `[prefs, setPrefs, prefsReady]`. `prefsReady` flips true once the
 * backend query settles (success OR error) — effects that must run exactly once
 * against the REAL prefs (sticky-filter hydration, default-view application)
 * gate on it instead of on mount.
 */
export function useSegmentViewPrefs(oid: string | null) {
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: segmentViewPrefsQueryKey(oid),
    queryFn: async () => {
      const { prefs } = await fetchSegmentViewPrefs()
      return sanitizeViewPrefs(prefs)
    },
    staleTime: 5 * 60_000,
    retry: 1,
  })

  // Paint cache while the query is in flight; also the offline fallback.
  const cached = useMemo(() => loadViewPrefs(oid), [oid])

  // Mirror every backend response into the cache so the next reload paints right.
  useEffect(() => {
    if (query.data) saveViewPrefs(oid, query.data)
  }, [query.data, oid])

  const { mutate: persist } = useMutation({
    mutationFn: saveSegmentViewPrefs,
    onError: () => {
      // Roaming write failed (offline / backend down): the optimistic cache +
      // localStorage keep this device consistent; the next successful save wins.
    },
  })

  const setPrefs = useCallback(
    (next: SegmentViewPrefs) => {
      const clean = sanitizeViewPrefs(next)
      queryClient.setQueryData(segmentViewPrefsQueryKey(oid), clean)
      saveViewPrefs(oid, clean)
      persist(clean)
    },
    [queryClient, oid, persist]
  )

  const prefs = query.data ?? cached
  const prefsReady = query.isSuccess || query.isError
  return [prefs, setPrefs, prefsReady] as const
}
